import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

/* -----------------------------
  Helpers
----------------------------- */
function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "upload":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3v10m0 0 4-4m-4 4-4-4"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 17v3h16v-3"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case "search":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M10.5 18a7.5 7.5 0 1 0 0-15 7.5 7.5 0 0 0 0 15Z"
            stroke="currentColor"
            strokeWidth="2.2"
          />
          <path
            d="M16.5 16.5 21 21"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "download":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3v10m0 0 4-4m-4 4-4-4"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 17v3h16v-3"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case "eye":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
          <path
            d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
            stroke="currentColor"
            strokeWidth="2.2"
          />
        </svg>
      );
    case "shield":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm-1 14-3-3 1.4-1.4L11 13.2l3.6-3.6L16 11l-5 5Z"
            fill="currentColor"
          />
        </svg>
      );
    default:
      return null;
  }
}

/* -----------------------------
  Mock data
----------------------------- */
const MOCK_FORMS = [
  {
    id: "f1",
    formName: "Sun Life - Extended Health Claim",
    submittedTo: "Dr. Emily Carter",
    appointment: "Physiotherapy • Toronto, ON • 10:30 AM",
    status: "Pending Provider Review",
    submittedAt: "2026-01-02",
    lastUpdated: "2 hours ago",
    signedFileReady: false,
  },
  {
    id: "f2",
    formName: "Manulife - Dental Reimbursement",
    submittedTo: "Dr. Liam Thompson",
    appointment: "Dentistry • Vancouver, BC • Tomorrow 4:30 PM",
    status: "Provider Completed",
    submittedAt: "2025-12-29",
    lastUpdated: "1 day ago",
    signedFileReady: true,
  },
  {
    id: "f3",
    formName: "Blue Cross - Medical Certificate",
    submittedTo: "Dr. Noah Patel",
    appointment: "ENT • Calgary, AB • Today 8:00 PM",
    status: "Additional Information Required",
    submittedAt: "2025-12-20",
    lastUpdated: "3 days ago",
    signedFileReady: false,
  },
  {
    id: "f4",
    formName: "Canada Life - Paramedical Claim",
    submittedTo: "Dr. Olivia Martin",
    appointment: "Orthopedics • Ottawa, ON • 1:00 PM",
    status: "Submitted",
    submittedAt: "2025-12-15",
    lastUpdated: "1 week ago",
    signedFileReady: false,
  },
  {
    id: "f5",
    formName: "Green Shield Canada - Specialist Consultation",
    submittedTo: "Dr. Ava Singh",
    appointment: "Cardiology • Edmonton, AB • Next Fri 11:00 AM",
    status: "Signed & Ready",
    submittedAt: "2025-12-08",
    lastUpdated: "2 weeks ago",
    signedFileReady: true,
  },
];


function StatusBadge({ status }) {
  const map = {
    "Pending Doctor Review": "bg-amber-50 text-amber-700 border-amber-200",
    "Needs More Info": "bg-rose-50 text-rose-700 border-rose-200",
    Submitted: "bg-sky-50 text-sky-700 border-sky-200",
    "Doctor Filled": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Signed & Ready": "bg-emerald-50 text-emerald-700 border-emerald-200",
  };

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
        map[status] || "bg-slate-50 text-slate-700 border-slate-200"
      )}
    >
      {status}
    </span>
  );
}
function MenuItem({ label, to, active }) {
    return (
      <Link
        to={to}
        className={[
          "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition",
          active
            ? "bg-primary/10 text-primary"
            : "text-text hover:bg-slate-50",
        ].join(" ")}
      >
        <span className="h-2 w-2 rounded-full bg-current opacity-60" />
        {label}
      </Link>
    );
  }
  
/* -----------------------------
  Table Row Actions
----------------------------- */
function RowActions({ row, onView, onDownload }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <button
        onClick={() => onView(row)}
        className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-xs font-semibold hover:bg-slate-50"
      >
        <Icon name="eye" className="h-4 w-4" />
        View
      </button>
      <button
        onClick={() => onDownload(row)}
        disabled={!row.signedFileReady}
        className="inline-flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
      >
        <Icon name="download" className="h-4 w-4" />
        Download
      </button>
    </div>
  );
}

/* -----------------------------
  Main Screen
----------------------------- */
function InsuranceList() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return MOCK_FORMS.filter((f) => {
      const matchesQuery = q
        ? `${f.formName} ${f.submittedTo} ${f.appointment} ${f.status}`.toLowerCase().includes(q)
        : true;

      const matchesStatus = status === "All" ? true : f.status === status;

      return matchesQuery && matchesStatus;
    });
  }, [query, status]);

  function onSubmitNew() {
    // Next: create /patient/insurance/submit screen (upload + select appointment)
    navigate("/patient/insurance/submit");
  }

  function onView(row) {
    // Next: details screen
    navigate(`/patient/insurance/${encodeURIComponent(row.id)}`);
  }

  function onDownload(row) {
    // Mock download
    alert(`Downloading signed form (mock): ${row.formName}`);
  }

  return (
    <div className="w-full">
      {/* Top */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Insurance</h1>
          <p className="mt-1 text-sm text-muted">
            Submit your insurance form, get it filled by the doctor, and download the signed copy.
          </p>
        </div>

        <button
          onClick={onSubmitNew}
          className="btn-primary-gradient inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white"
        >
          <Icon name="upload" className="h-5 w-5" />
          Submit Insurance Form
        </button>
      </div>

      {/* Controls */}
      <div className="mt-4 rounded-2xl border border-border bg-surface shadow-soft px-5 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
              <Icon name="search" className="h-4 w-4" />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search forms, doctors, appointments, status..."
              className="w-full rounded-2xl border border-border bg-white pl-10 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-xl border border-border bg-white px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option>All</option>
              <option>Submitted</option>
              <option>Pending Doctor Review</option>
              <option>Needs More Info</option>
              <option>Doctor Filled</option>
              <option>Signed & Ready</option>
            </select>
          </div>

          <div className="ml-auto text-xs text-muted">
            Showing <span className="font-semibold text-text">{filtered.length}</span> results
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 text-xs text-muted">
          <span className="text-primary">
            <Icon name="shield" className="h-4 w-4" />
          </span>
          Your documents are encrypted in transit and at rest (to be enforced after backend integration).
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 rounded-2xl border border-border bg-surface shadow-soft overflow-hidden">
        <div className="border-b border-border px-5 py-4 flex items-center justify-between">
          <div className="font-semibold">Submitted Forms</div>
          <Link
            to="/patient/dashboard"
            className="text-sm font-semibold text-primary hover:underline"
          >
            Back to Dashboard
          </Link>
        </div>

        {filtered.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-lg font-semibold">No insurance forms found</div>
            <p className="mt-2 text-sm text-muted">
              Try a different search or submit a new form.
            </p>
            <button
              onClick={onSubmitNew}
              className="mt-4 btn-primary-gradient px-6 py-3 text-sm font-semibold text-white"
            >
              Submit Insurance Form
            </button>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-[980px] w-full">
              <thead className="bg-slate-50 border-b border-border">
                <tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-semibold">Form Name</th>
                  <th className="px-5 py-3 font-semibold">Submitted To</th>
                  <th className="px-5 py-3 font-semibold">Appointment</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Submitted Date</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0 bg-white">
                    <td className="px-5 py-4">
                      <div className="font-semibold">{row.formName}</div>
                      <div className="mt-1 text-xs text-muted">
                        Last updated • {row.lastUpdated}
                      </div>
                    </td>

                    <td className="px-5 py-4 text-sm text-text">
                      {row.submittedTo}
                    </td>

                    <td className="px-5 py-4">
                      <div className="text-sm text-text">{row.appointment}</div>
                      <div className="mt-1 text-xs text-muted">
                        {row.signedFileReady
                          ? "Signed copy available"
                          : "Waiting on doctor / additional info"}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <StatusBadge status={row.status} />
                    </td>

                    <td className="px-5 py-4 text-sm text-text">
                      {row.submittedAt}
                    </td>

                    <td className="px-5 py-4">
                      <RowActions row={row} onView={onView} onDownload={onDownload} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Small footer hint */}
      <div className="mt-4 text-xs text-muted">
        Tip: If status is <span className="font-semibold text-text">Needs More Info</span>, open the form to upload missing documents.
      </div>
    </div>
  );
}


export default function Insurance() {
    return (
        <div className="grid grid-cols-12 gap-5">
      
          {/* MAIN CONTENT */}
          <main className="col-span-12 lg:col-span-12">
            {/* ✅ Keep your existing Insurance UI here */}
            <div className="w-full">
              <InsuranceList />
            </div>
          </main>
        </div>
      );
      
}